/**
 * Cold Outreach Email Campaign Templates
 * Comprehensive template library for B2B cold email campaigns
 *
 * Inspired by billion-dollar sales engagement companies:
 * - HubSpot, Outreach, Salesloft (prospecting & sequences)
 * - Apollo.io, ZoomInfo (data-driven outreach)
 * - Gong (conversation intelligence & best practices)
 * - Demandbase, 6sense, Terminus (ABM strategies)
 *
 * Total: 42 campaign templates across 8 categories
 */

// All imports must come first
import { PROSPECTING_TEMPLATES } from "./prospecting";
import { FOLLOWUP_TEMPLATES } from "./followup";
import { MEETING_TEMPLATES } from "./meeting";
import { EVENT_TEMPLATES } from "./eventBased";
import { ABM_TEMPLATES } from "./abm";
import { REENGAGEMENT_TEMPLATES } from "./reengagement";
import { INDUSTRY_TEMPLATES } from "./industry";
import { PARTNER_TEMPLATES } from "./partner";
import {
  CampaignTemplate,
  CampaignTemplateCategory,
  CampaignCategory,
  TargetAudience,
  PersonalizationLevel,
  CATEGORY_METADATA,
} from "./types";

// Type exports
export * from "./types";

// Re-export individual template arrays
export { PROSPECTING_TEMPLATES } from "./prospecting";
export { FOLLOWUP_TEMPLATES } from "./followup";
export { MEETING_TEMPLATES } from "./meeting";
export { EVENT_TEMPLATES } from "./eventBased";
export { ABM_TEMPLATES } from "./abm";
export { REENGAGEMENT_TEMPLATES } from "./reengagement";
export { INDUSTRY_TEMPLATES } from "./industry";
export { PARTNER_TEMPLATES } from "./partner";

/**
 * All campaign templates combined
 */
export const ALL_CAMPAIGN_TEMPLATES: CampaignTemplate[] = [
  ...PROSPECTING_TEMPLATES,
  ...FOLLOWUP_TEMPLATES,
  ...MEETING_TEMPLATES,
  ...EVENT_TEMPLATES,
  ...ABM_TEMPLATES,
  ...REENGAGEMENT_TEMPLATES,
  ...INDUSTRY_TEMPLATES,
  ...PARTNER_TEMPLATES,
];

/**
 * Campaign template categories for gallery display
 */
export const CAMPAIGN_TEMPLATE_CATEGORIES: CampaignTemplateCategory[] = [
  {
    id: "prospecting",
    name: CATEGORY_METADATA.prospecting.name,
    description: CATEGORY_METADATA.prospecting.description,
    icon: CATEGORY_METADATA.prospecting.icon,
    color: CATEGORY_METADATA.prospecting.color,
    templates: PROSPECTING_TEMPLATES,
  },
  {
    id: "followup",
    name: CATEGORY_METADATA.followup.name,
    description: CATEGORY_METADATA.followup.description,
    icon: CATEGORY_METADATA.followup.icon,
    color: CATEGORY_METADATA.followup.color,
    templates: FOLLOWUP_TEMPLATES,
  },
  {
    id: "meeting",
    name: CATEGORY_METADATA.meeting.name,
    description: CATEGORY_METADATA.meeting.description,
    icon: CATEGORY_METADATA.meeting.icon,
    color: CATEGORY_METADATA.meeting.color,
    templates: MEETING_TEMPLATES,
  },
  {
    id: "event",
    name: CATEGORY_METADATA.event.name,
    description: CATEGORY_METADATA.event.description,
    icon: CATEGORY_METADATA.event.icon,
    color: CATEGORY_METADATA.event.color,
    templates: EVENT_TEMPLATES,
  },
  {
    id: "abm",
    name: CATEGORY_METADATA.abm.name,
    description: CATEGORY_METADATA.abm.description,
    icon: CATEGORY_METADATA.abm.icon,
    color: CATEGORY_METADATA.abm.color,
    templates: ABM_TEMPLATES,
  },
  {
    id: "reengagement",
    name: CATEGORY_METADATA.reengagement.name,
    description: CATEGORY_METADATA.reengagement.description,
    icon: CATEGORY_METADATA.reengagement.icon,
    color: CATEGORY_METADATA.reengagement.color,
    templates: REENGAGEMENT_TEMPLATES,
  },
  {
    id: "industry",
    name: CATEGORY_METADATA.industry.name,
    description: CATEGORY_METADATA.industry.description,
    icon: CATEGORY_METADATA.industry.icon,
    color: CATEGORY_METADATA.industry.color,
    templates: INDUSTRY_TEMPLATES,
  },
  {
    id: "partner",
    name: CATEGORY_METADATA.partner.name,
    description: CATEGORY_METADATA.partner.description,
    icon: CATEGORY_METADATA.partner.icon,
    color: CATEGORY_METADATA.partner.color,
    templates: PARTNER_TEMPLATES,
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get a campaign template by its ID
 * @param id - Template ID (e.g., "prospect-problem-aware")
 * @returns The matching template or undefined
 */
export function getCampaignTemplateById(
  id: string
): CampaignTemplate | undefined {
  return ALL_CAMPAIGN_TEMPLATES.find((template) => template.id === id);
}

/**
 * Get all templates for a specific category
 * @param category - Campaign category
 * @returns Array of templates in that category
 */
export function getCampaignTemplatesByCategory(
  category: CampaignCategory
): CampaignTemplate[] {
  return ALL_CAMPAIGN_TEMPLATES.filter(
    (template) => template.category === category
  );
}

/**
 * Get all templates for a specific target audience
 * @param audience - Target audience type
 * @returns Array of templates targeting that audience
 */
export function getCampaignTemplatesByAudience(
  audience: TargetAudience
): CampaignTemplate[] {
  return ALL_CAMPAIGN_TEMPLATES.filter(
    (template) => template.targetAudience === audience
  );
}

/**
 * Get all templates with a specific tag
 * @param tag - Tag to search for
 * @returns Array of templates with that tag
 */
export function getCampaignTemplatesByTag(tag: string): CampaignTemplate[] {
  const normalizedTag = tag.toLowerCase();
  return ALL_CAMPAIGN_TEMPLATES.filter((template) =>
    template.tags.some((t) => t.toLowerCase() === normalizedTag)
  );
}

/**
 * Get all templates by difficulty level
 * @param difficulty - Difficulty level
 * @returns Array of templates at that difficulty
 */
export function getCampaignTemplatesByDifficulty(
  difficulty: "beginner" | "intermediate" | "advanced"
): CampaignTemplate[] {
  return ALL_CAMPAIGN_TEMPLATES.filter(
    (template) => template.difficulty === difficulty
  );
}

/**
 * Get all templates by personalization level
 * @param level - Personalization level required
 * @returns Array of templates at that personalization level
 */
export function getCampaignTemplatesByPersonalization(
  level: PersonalizationLevel
): CampaignTemplate[] {
  return ALL_CAMPAIGN_TEMPLATES.filter(
    (template) => template.personalizationLevel === level
  );
}

/**
 * Search campaign templates by query string
 * Searches name, description, tags, and bestFor fields
 * @param query - Search query
 * @returns Array of matching templates
 */
export function searchCampaignTemplates(query: string): CampaignTemplate[] {
  const normalizedQuery = query.toLowerCase().trim();

  if (!normalizedQuery) {
    return ALL_CAMPAIGN_TEMPLATES;
  }

  return ALL_CAMPAIGN_TEMPLATES.filter((template) => {
    const searchableText = [
      template.name,
      template.description,
      template.strategy,
      ...template.tags,
      ...template.bestFor,
      template.category,
      template.targetAudience,
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedQuery);
  });
}

/**
 * Get templates best suited for a specific use case
 * @param useCase - Use case to match (e.g., "SDR Teams", "Enterprise Sales")
 * @returns Array of templates best for that use case
 */
export function getCampaignTemplatesForUseCase(
  useCase: string
): CampaignTemplate[] {
  const normalizedUseCase = useCase.toLowerCase();
  return ALL_CAMPAIGN_TEMPLATES.filter((template) =>
    template.bestFor.some((bf) => bf.toLowerCase().includes(normalizedUseCase))
  );
}

/**
 * Get templates that reference a specific company/methodology as inspiration
 * @param inspiration - Company or methodology (e.g., "Gong", "MEDDIC")
 * @returns Array of templates inspired by that source
 */
export function getCampaignTemplatesByInspiration(
  inspiration: string
): CampaignTemplate[] {
  const normalizedInspiration = inspiration.toLowerCase();
  return ALL_CAMPAIGN_TEMPLATES.filter((template) =>
    template.inspiredBy?.some((i) =>
      i.toLowerCase().includes(normalizedInspiration)
    )
  );
}

/**
 * Get template statistics
 * @returns Statistics about the template library
 */
export function getCampaignTemplateStats(): {
  total: number;
  byCategory: Record<CampaignCategory, number>;
  byAudience: Record<TargetAudience, number>;
  byDifficulty: Record<"beginner" | "intermediate" | "advanced", number>;
  byPersonalization: Record<PersonalizationLevel, number>;
} {
  const stats = {
    total: ALL_CAMPAIGN_TEMPLATES.length,
    byCategory: {} as Record<CampaignCategory, number>,
    byAudience: {} as Record<TargetAudience, number>,
    byDifficulty: {} as Record<"beginner" | "intermediate" | "advanced", number>,
    byPersonalization: {} as Record<PersonalizationLevel, number>,
  };

  ALL_CAMPAIGN_TEMPLATES.forEach((template) => {
    // Count by category
    stats.byCategory[template.category] =
      (stats.byCategory[template.category] || 0) + 1;

    // Count by audience
    stats.byAudience[template.targetAudience] =
      (stats.byAudience[template.targetAudience] || 0) + 1;

    // Count by difficulty
    stats.byDifficulty[template.difficulty] =
      (stats.byDifficulty[template.difficulty] || 0) + 1;

    // Count by personalization
    stats.byPersonalization[template.personalizationLevel] =
      (stats.byPersonalization[template.personalizationLevel] || 0) + 1;
  });

  return stats;
}

/**
 * Get all unique tags across all templates
 * @returns Array of unique tags, sorted alphabetically
 */
export function getAllCampaignTags(): string[] {
  const tagSet = new Set<string>();
  ALL_CAMPAIGN_TEMPLATES.forEach((template) => {
    template.tags.forEach((tag) => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
}

/**
 * Get all unique "bestFor" values across all templates
 * @returns Array of unique use cases, sorted alphabetically
 */
export function getAllCampaignUseCases(): string[] {
  const useCaseSet = new Set<string>();
  ALL_CAMPAIGN_TEMPLATES.forEach((template) => {
    template.bestFor.forEach((useCase) => useCaseSet.add(useCase));
  });
  return Array.from(useCaseSet).sort();
}

/**
 * Get all unique inspirations across all templates
 * @returns Array of unique inspiration sources, sorted alphabetically
 */
export function getAllCampaignInspirations(): string[] {
  const inspirationSet = new Set<string>();
  ALL_CAMPAIGN_TEMPLATES.forEach((template) => {
    template.inspiredBy?.forEach((inspiration) =>
      inspirationSet.add(inspiration)
    );
  });
  return Array.from(inspirationSet).sort();
}

/**
 * Filter templates with multiple criteria
 * @param filters - Filter criteria
 * @returns Array of templates matching all criteria
 */
export function filterCampaignTemplates(filters: {
  category?: CampaignCategory;
  audience?: TargetAudience;
  difficulty?: "beginner" | "intermediate" | "advanced";
  personalization?: PersonalizationLevel;
  tags?: string[];
  search?: string;
}): CampaignTemplate[] {
  let results = [...ALL_CAMPAIGN_TEMPLATES];

  if (filters.category) {
    results = results.filter((t) => t.category === filters.category);
  }

  if (filters.audience) {
    results = results.filter((t) => t.targetAudience === filters.audience);
  }

  if (filters.difficulty) {
    results = results.filter((t) => t.difficulty === filters.difficulty);
  }

  if (filters.personalization) {
    results = results.filter(
      (t) => t.personalizationLevel === filters.personalization
    );
  }

  if (filters.tags && filters.tags.length > 0) {
    const normalizedTags = filters.tags.map((t) => t.toLowerCase());
    results = results.filter((t) =>
      normalizedTags.some((tag) =>
        t.tags.some((templateTag) => templateTag.toLowerCase() === tag)
      )
    );
  }

  if (filters.search) {
    const query = filters.search.toLowerCase().trim();
    results = results.filter((t) => {
      const searchableText = [
        t.name,
        t.description,
        ...t.tags,
        ...t.bestFor,
      ]
        .join(" ")
        .toLowerCase();
      return searchableText.includes(query);
    });
  }

  return results;
}

/**
 * Sort templates by various criteria
 * @param templates - Templates to sort
 * @param sortBy - Sort criteria
 * @param order - Sort order
 * @returns Sorted array of templates
 */
export function sortCampaignTemplates(
  templates: CampaignTemplate[],
  sortBy: "name" | "difficulty" | "emails" | "category" = "name",
  order: "asc" | "desc" = "asc"
): CampaignTemplate[] {
  const sorted = [...templates].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case "name":
        comparison = a.name.localeCompare(b.name);
        break;
      case "difficulty":
        const difficultyOrder = { beginner: 0, intermediate: 1, advanced: 2 };
        comparison = difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
        break;
      case "emails":
        comparison = a.emails.length - b.emails.length;
        break;
      case "category":
        comparison = a.category.localeCompare(b.category);
        break;
    }

    return order === "desc" ? -comparison : comparison;
  });

  return sorted;
}

// Default export for convenience
export default {
  templates: ALL_CAMPAIGN_TEMPLATES,
  categories: CAMPAIGN_TEMPLATE_CATEGORIES,
  getById: getCampaignTemplateById,
  getByCategory: getCampaignTemplatesByCategory,
  getByAudience: getCampaignTemplatesByAudience,
  getByTag: getCampaignTemplatesByTag,
  getByDifficulty: getCampaignTemplatesByDifficulty,
  getByPersonalization: getCampaignTemplatesByPersonalization,
  search: searchCampaignTemplates,
  filter: filterCampaignTemplates,
  sort: sortCampaignTemplates,
  getStats: getCampaignTemplateStats,
  getAllTags: getAllCampaignTags,
  getAllUseCases: getAllCampaignUseCases,
  getAllInspirations: getAllCampaignInspirations,
};
